<template>
  <div id="quick-reply-wrapper">
    <h1 class="border-bottom">Quick replies</h1>
    <div class="divider dotted-border" />
    <div
      ref="replyContainer"
      class="quick-replies scroll-area">
      <div
        v-for="(reply, index) in filteredQuickReplies"
        :key="reply.id" >
        <template v-if="reply.type === 'prelist'">
          <h3
            v-if="
              (index === 0 && reply.type === 'prelist') ||
                (index > 0 && filteredQuickReplies[index - 1].type !== reply.type && reply.type === 'prelist')
            ">{{ reply.section }}</h3>
          <div
            class="reply-item new-quick-reply flex flex-align-center prelist"
            :class="{
              hover: !mouseHoverActive && filteredQuickReplies[selectedId] === reply,
              'prevent-mouse-hover': !mouseHoverActive,
            }"
            @click="runItemAction(index)"
            @mouseover="mouseOverAction(index)"
            @mouseout="mouseOutAction"
            @mousemove="resetMouse(index)" >
            <span class="greedy ellipsis flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.bluePrimary"
                background-color="#e5f4ff"
                :size="8"
                :hover-size="18"
                :border-radius="50" />
              <p class="ellipsis">{{ reply.text }} <b v-if="search">{{ search }}</b></p>
            </span>
            <span
              v-if="filteredQuickReplies[selectedId] === reply"
              class="enter-marker flex center-all ml-3" >
              <span>Enter</span>
              <SBIcon
                class="ml-1"
                :icon="SBIconList.DONE_TICK"
                :color="SBColors.blackLight"
                :size="6"/>
            </span>
          </div>
        </template>
        <template v-else>
          <h3
            v-if="
              (index === 0 && reply.type !== 'prelist') ||
                (index > 0 && filteredQuickReplies[index - 1].type !== reply.type)"
          >{{ reply.section ? reply.section : 'All' }}</h3>
          <div
            :ref="`reply-${reply.id}`"
            class="reply-item flex flex-align-center"
            :class="{
              hover: !mouseHoverActive && filteredQuickReplies[selectedId] === reply,
              'prevent-mouse-hover': !mouseHoverActive,
            }"
            @click="runItemAction(index)"
            @mouseover="mouseOverAction(index)"
            @mouseout="mouseOutAction"
            @mousemove="resetMouse(index)" >
            <span class="greedy ellipsis tag-content">
              <h2 class="ellipsis">{{ reply.keyword }}</h2>
              <p
                class="message ellipsis"
                :inner-html.prop="flattenHTML(whatsAppFormatToHTML(reply.text))" />
            </span>
            <span
              v-if="filteredQuickReplies[selectedId] === reply"
              class="enter-marker flex center-all ml-3" >
              <span>Enter</span>
              <SBIcon
                class="ml-1"
                :icon="SBIconList.DONE_TICK"
                :color="SBColors.blackLight"
                :size="6"/>
            </span>
          </div>
        </template>
      </div>
      <template v-if="maxNumberOfQuickRepliesReached && !filteredQuickReplies.length">
        <p class="no-data">
          <span class="emoji emoji-fix">😐</span> We couldn't find anything for
          <b>{{ search }}</b>
        </p>
      </template>
    </div>
    <QuickReplyEditorModal
      ref="quickReplyEditorModalRef" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { scrollIntoViewIfNeeded } from '../../../../helpers/scrollHelpers';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import QuickReplyEditorModal from '@/components/business/chat/modals/QuickReplyEditor.modal';
import { whatsAppFormatToHTML, flattenHTML } from '@/helpers/stringHelpers';

export default {
  name: 'QuickReplies',
  components: {
    QuickReplyEditorModal,
    SBIcon,
  },
  props: {
    search: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      mouseHoverActive: false,
      quickReplies: [],
      activeReply: {},
      selectedId: 0,
      ignoreMouse: false,
      maxNumberOfQuickReplies: 500,
      preventOutsideClick: false,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      quickRepliesLoaded: 'chat/quickRepliesLoaded',
      quickReplyList: 'chat/quickReplyList',
      merchantName: 'merchant/merchantName',
      agentName: 'user/name',
      agentFirstName: 'user/firstName',
      contactData: 'contactDetails/contactData',
      customFieldList: 'contact/customFieldList',
      currentOrganisation: 'organisation/currentOrganisation',
      activeConversation: 'chat/activeConversation',
    }),
    filteredQuickReplies() {
      const preList = [];
      const matchingQuickReply = this.quickReplyList.find(qr => qr.keyword.toLowerCase() === this.search.toLowerCase());
      const filteredList = this.quickReplyList.filter(qr => qr.keyword.toLowerCase().includes(this.search.toLowerCase()) || qr.text.toLowerCase().includes(this.search.toLowerCase()));
      if (!this.maxNumberOfQuickRepliesReached && (!filteredList.length || !this.search)) { //&& !matchingQuickReply
        preList.push({
          type: 'prelist',
          text: 'Create new quick reply',
          section: 'New',
          action: this.createNewQuickReply,
        });
      }

      // prepend create new
      return [
        ...preList,
        ...filteredList,
      ];
    },
    // TODO check if needed
    messageTagValues() {
      return [
        {
          keyword: '{{business_name}}',
          value: this.merchantName,
        },
        {
          keyword: '{{contact_name}}',
          value: this.contact.name,
        },
        {
          keyword: '{{agent_first_name}}',
          value: this.agentFirstName,
        },
        {
          keyword: '{{agent_name}}',
          value: this.agentName,
        },
      ];
    },
    maxNumberOfQuickRepliesReached() {
      return this.quickReplyList.length >= this.maxNumberOfQuickReplies;
    },
  },
  watch: {
    filteredQuickReplies() {
      if (this.selectedId >= this.filteredQuickReplies.length) {
        this.selectedId = this.filteredQuickReplies.length - 1;
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
    this.$nextTick(() => {
      const countPrelist = this.filteredQuickReplies.filter(qr => qr.type === 'prelist').length;
      if (this.selectedId === 0 && countPrelist && this.filteredQuickReplies.length > countPrelist) {
        this.selectedId = countPrelist;
      }
    });

    this.$root.$on('bv::modal::show', () => {
      this.preventOutsideClick = true;
    });
    this.$root.$on('bv::modal::hidden', () => {
      this.preventOutsideClick = false;
    });
  },
  destroyed() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    ...mapActions({}),
    whatsAppFormatToHTML,
    flattenHTML,
    mouseOverAction(itemId) {
      if (!this.ignoreMouse) {
        this.select(itemId);
      }
    },
    mouseOutAction() {
      if (!this.ignoreMouse) {
        this.mouseHoverActive = false;
      }
    },
    resetMouse(itemId) {
      this.ignoreMouse = false;
      this.select(itemId);
    },
    select(itemId) {
      this.selectedId = itemId;
      scrollIntoViewIfNeeded(this.$refs.replyContainer, this.$refs.replyContainer.children[itemId]);
    },
    handleKeyDown(event) {
      this.ignoreMouse = true;
      switch (event.keyCode) {
        // UP arrow
        case 38:
          this.mouseHoverActive = false;
          if (this.selectedId > 0) {
            this.select(this.selectedId - 1);
          }
          event.preventDefault();
          break;
        // DOWN arrow
        case 40:
          this.mouseHoverActive = false;
          if (this.selectedId < this.filteredQuickReplies.length - 1) {
            this.select(this.selectedId + 1);
          }
          event.preventDefault();
          break;
        case 13:
          // run item action
          event.preventDefault();
          this.runItemAction(this.selectedId);
          break;
        case 27: // ESC
          if (!this.preventOutsideClick) {
            this.$emit('abort');
          } else {
            this.$emit('focus');
          }
          break;
        default:
          break;
      }
    },
    // TODO check if needed
    isActiveReply(reply) {
      return this.activeReply && (reply.id === this.activeReply.id);
    },
    runItemAction(index) {
      if (this.filteredQuickReplies[index].action) {
        this.filteredQuickReplies[index].action();
      } else {
        this.selectReply(this.filteredQuickReplies[index]);
      }
    },
    // TOD check this
    selectReply(reply) {
      if (this.$lodash.isEmpty(reply)) {
        return;
      }
      this.$emit('replySelected', whatsAppFormatToHTML(this.changeTagsToValues(reply.text)));
    },
    // TOD check this
    changeTagsToValues(reply) {
      const foundTags = reply.match(/{{_.*?_}}/g) || [];
      foundTags.forEach((tag) => {
        const tagId = tag.replace(/^{{_(.*)_}}$/, '$1');
        let returnValue;
        let field;
        try {
          field = this.customFieldList[this.currentOrganisation.id].find(customField => customField.id === tagId);
          const fieldValue = this.contactData.meta_data.custom_fields[tagId];
          returnValue = fieldValue;
        } catch (error) {
          returnValue = '';
        }

        // Special cases when we do not have the value in meta data but we need to render it like date which is epoch and need to be converted to human readable format
        // ---------------------------
        if (returnValue && field && field.type) {
          switch (field.type) {
            case 'date':
              returnValue = this.$options.filters.dateCalendar(returnValue, this.contactData.meta_data.country.code);
              break;
            case 'list':
              try {
                const targetOption = field.options.find(o => o.value === returnValue);
                if (targetOption) {
                  returnValue = targetOption.name ? targetOption.name : '';
                }
              } catch (error) {
                returnValue = '';
              }
              break;
            case 'boolean':
              returnValue = Number(returnValue) ? 'Yes' : 'No';
              break;
            default:
              break;
          }
        } else if (!returnValue) {
          returnValue = '';
        }
        reply = reply.replace(new RegExp(`${tag}[ ]{0,1}`, 'g'), returnValue);
      });

      const foundDefaultTags = reply.match(/{{.*?}}/g) || [];
      foundDefaultTags.forEach((dt) => {
        if (dt === '{{contact_name}}') {
          reply = reply.replace(new RegExp(`${dt}[ ]{0,1}`, 'g'), this.activeConversation.contact.name);
        }
        if (dt === '{{business_name}}') {
          reply = reply.replace(new RegExp(`${dt}[ ]{0,1}`, 'g'), this.activeConversation.business_name);
        }
      });
      // IF some tags are still present then it means they are deleted or unavailable for some other reason.
      // Aggreament is that we will just remove it in this case and print an empty string instead.
      reply = reply.replaceAll(/{{.*?}}\s{0,1}/g, '');
      return reply;
    },
    createNewQuickReply() {
      this.$refs.quickReplyEditorModalRef.show({ keyword: this.search });
    },
  },
};
</script>

<style lang="scss" scoped>
#quick-reply-wrapper {
  max-width: 1024px;
  width: 100%;
  padding: 0;
  border-radius: 5px;
  box-shadow: 0 2px 5px 0 rgba(13, 15, 22, 0.23);
  background-color: $white;
  z-index: 999;

  h1 {
    padding: 15px;
    font-size: 14px;
    font-weight: 500;
    text-align: center;
  }

  h3 {
    font-size: 12px;
    font-weight: 500;
    color: $greyPrimary;
    padding: 10px 15px 5px;
  }

  .quick-replies {
    padding: 4px 0 7px;
    overflow-y: auto;
    max-height: 300px;
  }

  .reply-item {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
    padding: 9px 15px;
    cursor: pointer;
    min-height: 39px;

    &.hover,
    &:not(.prevent-mouse-hover):hover {
      background-color: $hover;
    }

    h2 {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.2;
    }

    p {
      font-size: 12px;
      font-weight: 400;
      line-height: 1.2;
      color: $blackLight;
      margin-top: 3px;
    }

    &.prelist {
      p {
        font-size: 14px;
        color: $blackPrimary;
        margin-top: 1px;
      }
    }

    .enter-marker {
      color: $blackLight;
      font-size: 10px;
      font-weight: 500;
      padding: 4px 6px 3px 6px;
      border-radius: 5px;
      border: solid 1px #d7dbdd;
      background-color: var(--white);
    }
  }

  .no-data {
    font-size: 14px;
    border-top: none !important;
    // width: fit-content;
    padding: 10px 18px 10px 12px;
    cursor: default;
    text-align: center;
    color: $blackLight;

    .emoji {
      font-size: 16px;
      font-family: 'apple color emoji,android emoji,emojione mozilla,twemoji mozilla,segoe ui emoji,noto color emoji,emojisymbols,segoe ui symbol';
    }

    b {
      font-weight: 500;
    }
  }
}
</style>
